import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const RDIServerInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';

  const publicIp = annotations['rdi.infrastructure/public-ip'] || annotations['rdi-server.infrastructure/public-ip'];
  const instanceType = annotations['rdi.infrastructure/instance-type'] || annotations['rdi-server.infrastructure/instance-type'];
  const awsRegion = annotations['aws.infrastructure/region'] || annotations['rdi-server.infrastructure/aws-region'];
  const consoleUrl = annotations['rdi.infrastructure/console-url'] || annotations['rdi-server.infrastructure/console-url'];
  const vpcId = annotations['rdi.infrastructure/vpc-id'] || annotations['aws.infrastructure/vpc-id'];
  const securityGroupId = annotations['rdi.infrastructure/security-group-id'] || annotations['aws.infrastructure/security-group-id'];

  const rdiVersion = annotations['rdi.infrastructure/version'] || annotations['rdi-server.infrastructure/rdi-version'];
  const rdiApiUrl = annotations['rdi.infrastructure/rdi-api-url'] || annotations['rdi-server.infrastructure/rdi-api-url'];
  const debeziumVersion = annotations['rdi.infrastructure/debezium-version'] || annotations['rdi-server.infrastructure/debezium-version'];

  const rdiDatabaseHost = annotations['rdi.infrastructure/rdi-database-host'] || annotations['rdi-server.infrastructure/rdi-database-host'];
  const rdiDatabasePort = annotations['rdi.infrastructure/rdi-database-port'] || annotations['rdi-server.infrastructure/rdi-database-port'];
  const rdiDatabasePassword = annotations['rdi.infrastructure/rdi-database-password'] || annotations['rdi-server.infrastructure/rdi-database-password'];

  const sourceDbType = annotations['rdi.infrastructure/source-db-type'] || annotations['rdi-server.infrastructure/source-db-type'];
  const sourceDbHost = annotations['rdi.infrastructure/source-db-host'] || annotations['rdi-server.infrastructure/source-db-host'];
  const sourceDbPort = annotations['rdi.infrastructure/source-db-port'] || annotations['rdi-server.infrastructure/source-db-port'];
  const sourceDbName = annotations['rdi.infrastructure/source-db-name'] || annotations['rdi-server.infrastructure/source-db-name'];
  const sourceDbUser = annotations['rdi.infrastructure/source-db-user'] || annotations['rdi-server.infrastructure/source-db-user'];
  const sourceDbPassword = annotations['rdi.infrastructure/source-db-password'] || annotations['rdi-server.infrastructure/source-db-password'];

  const targetRedisHost = annotations['rdi.infrastructure/target-redis-host'] || annotations['rdi-server.infrastructure/target-redis-host'];
  const targetRedisPort = annotations['rdi.infrastructure/target-redis-port'] || annotations['rdi-server.infrastructure/target-redis-port'];
  const targetRedisPassword = annotations['rdi.infrastructure/target-redis-password'] || annotations['rdi-server.infrastructure/target-redis-password'];

  const sshCommand = annotations['rdi.infrastructure/ssh-command'] || annotations['rdi-server.infrastructure/ssh-command'];
  const sshPrivateKeyEncoded = annotations['rdi.infrastructure/ssh-private-key'] || annotations['rdi-server.infrastructure/ssh-private-key'];

  const rdiStatusCommand = annotations['rdi.infrastructure/rdi-status-command'] || annotations['rdi-server.infrastructure/rdi-status-command'];

  if (templateType !== 'aws-rdi-server' || !publicIp) return null;

  let sshPrivateKey = '';
  if (sshPrivateKeyEncoded) {
    try {
      sshPrivateKey = atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH private key:', e);
    }
  }

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'rdi_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // redis-di 1.19+ is an API client: `describe` replaced `status`, `info` checks API connectivity.
  const statusCmd = rdiStatusCommand || 'redis-di describe';
  const apiCheckCmd = 'redis-di info';

  return (
    <InfoCard title="🔄 RDI Server Information" icon={<RiDatabase2Line size={20} />}>
      <InfraStack>
        <InfraSection title="☁️ RDI Server">
          {rdiVersion && (
            <InfraField
              label="RDI Version:"
              value={
                <InfraTagRow>
                  <InfraTag>{rdiVersion}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {debeziumVersion && (
            <InfraField
              label="Debezium Version:"
              value={
                <InfraTagRow>
                  <InfraTag>{debeziumVersion}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {instanceType && (
            <InfraField
              label="Instance Type:"
              value={
                <InfraTagRow>
                  <InfraTag>{instanceType}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {awsRegion && (
            <InfraField
              label="AWS Region:"
              value={
                <InfraTagRow>
                  <InfraTag>{awsRegion}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {publicIp && <CopyableValue label="Public IP:" value={publicIp} />}
          {rdiApiUrl && <CopyableValue label="RDI API:" value={rdiApiUrl} />}
          {consoleUrl && (
            <InfraField
              label="AWS Console:"
              value={
                <ExternalLink href={consoleUrl}>View in AWS Console</ExternalLink>
              }
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title={`📊 Source Database (${sourceDbType})`}>
          {sourceDbHost && <CopyableValue label="Host:" value={sourceDbHost} />}
          {sourceDbPort && <InfraField label="Port:" value={sourceDbPort} />}
          {sourceDbName && <InfraField label="Database:" value={sourceDbName} />}
          {sourceDbUser && <InfraField label="User:" value={sourceDbUser} />}
          {sourceDbPassword && (
            <CopyableValue label="Password:" value={sourceDbPassword} />
          )}
        </InfraSection>

        <InfraSection title="🔴 RDI Database">
          {rdiDatabaseHost && <CopyableValue label="Host:" value={rdiDatabaseHost} />}
          {rdiDatabasePort && <InfraField label="Port:" value={rdiDatabasePort} />}
          {rdiDatabasePassword && (
            <CopyableValue label="Password:" value={rdiDatabasePassword || '(empty)'} />
          )}
        </InfraSection>

        <InfraSection title="🎯 Target Redis">
          {targetRedisHost && <CopyableValue label="Host:" value={targetRedisHost} />}
          {targetRedisPort && <InfraField label="Port:" value={targetRedisPort} />}
          {targetRedisPassword && (
            <CopyableValue label="Password:" value={targetRedisPassword || '(empty)'} />
          )}
        </InfraSection>

        {(vpcId || securityGroupId) && (
          <>
            <InfraDivider />
            <InfraSection title="🌐 Network">
              {vpcId && <CopyableValue label="VPC ID:" value={vpcId} />}
              {securityGroupId && <CopyableValue label="Security Group:" value={securityGroupId} />}
            </InfraSection>
          </>
        )}

        {sshCommand && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              <CopyableValue label="SSH Command:" value={sshCommand} />
              {sshPrivateKey && (
                <Flex gap="2" style={{ marginTop: '0.5rem' }}>
                  <Button variant="primary" size="small" onPress={downloadSSHKey}>
                    <RiDownloadLine size={16} />
                    Download Key
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                  >
                    <RiFileCopyLine size={16} />
                    Copy Key
                  </Button>
                </Flex>
              )}
              <Text variant="body-small" style={{ display: 'block', marginTop: '0.5rem' }}>
                💡 Save as rdi_key.pem and run: <code>chmod 600 rdi_key.pem</code>
              </Text>
            </InfraSection>
          </>
        )}

        <InfraDivider />
        <InfraSection title="🚀 Quick Commands">
          <Text variant="title-small">Check RDI Status:</Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock>{statusCmd}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={statusCmd} />
            </div>
          </div>
          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            Check RDI API:
          </Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock>{apiCheckCmd}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={apiCheckCmd} />
            </div>
          </div>
          {rdiApiUrl && (
            <Text variant="body-small" style={{ display: 'block', marginTop: '0.5rem' }}>
              💡 Redis Insight → Redis Data Integration → add endpoint <code>{rdiApiUrl}</code> (self-signed cert)
            </Text>
          )}
        </InfraSection>
      </InfraStack>
    </InfoCard>
  );
};
